import * as React from 'react'
// import { vsprintf } from 'sprintf-js'

import {
  ProductSort,
  SimpleFacet,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  styled,
  spacing,
  mq,
  ZIndexLevel,
} from '@thg-commerce/enterprise-theme'
import { OptionsType } from '@thg-commerce/gravity-elements/CustomDropdown/CustomDropdown'
import {
  BlankDropdown,
  Button,
  CustomDropdown,
} from '@thg-commerce/gravity-elements'

import { clearFacetSelection, displayFacet } from '../utils'
import { FacetsProps, Facet } from '../types'
import { FacetContent } from '../FacetContent/FacetContent'
import { BrandFacet } from '../FacetComponents'

export const HorizontalFacetWrapper = styled.div`
  display: flex;
  max-width: 100%;
  margin: ${spacing(4)} 0 ${spacing(2)};
  border-top: 1px solid ${(props) => props.theme.colors.palette.greys.light};
  border-bottom: 1px solid ${(props) => props.theme.colors.palette.greys.light};
  padding: ${spacing(3)} 0 ${spacing(1)};
  align-items: center;
`

export const FacetContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  position: static;
  flex: 0 1 auto;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  flex-direction: row;
  width: 100%;
  row-gap: ${spacing(2)};
  column-gap: ${spacing(4)};
  overflow: hidden;
`
//   ${/*(props) =>
//      props.isOpen
//        ? `max-height: none;  margin-right: ${spacing(3)};`
//        : `max-height: 48px`*/};
// `

export const StyledBlankDropdown = styled(BlankDropdown)<{
  isBrandFacet: boolean
}>`
  max-width: 210px;
  min-width: 160px;
  flex-grow: 1;
  margin-bottom: ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    min-width: 164px;
  }

  & > div > div {
    ${(props) =>
      props.isBrandFacet && {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
  }
`

export const ButtonContainer = styled.div<{ isOpen: boolean }>`
  display: flex;
  flex-shrink: 0;
  flex-wrap: nowrap;
  column-gap: ${spacing(2)};
  margin-left: ${spacing(2)};
  ${(props) =>
    props.isOpen &&
    `flex-wrap: wrap; row-gap: ${spacing(4)}; flex-direction: column-reverse;`}
`

export const FilterButton = styled(Button)<{ show: boolean }>`
  min-width: max-content;
  display: ${(props) => (props.show ? 'block' : 'none')};
`

export const ClearAllButton = styled(Button)`
  min-width: max-content;
`

export interface HorizontalFacetsProps extends FacetsProps {
  i18nText: {
    searchBrandLabel: string
    searchBrandPlaceholder: string
    filterText: string
    close: string
    clearAll: string
    sortBy: string
    minimumLabel: string
    maximumLabel: string
    sliderHeader: string
  }
  sortTypes: {
    type: ProductSort
    displayText: string
  }[]
  dropDownOptions: OptionsType[]
  hasBrandSearch?: boolean
  selectedSort?: string | number
}

// const isOverflown = ({
//   clientWidth,
//   clientHeight,
//   scrollWidth,
//   scrollHeight,
// }) => {
//   return scrollHeight > clientHeight || scrollWidth > clientWidth
// }

export const HorizontalFacets = (props: HorizontalFacetsProps) => {
  const [showFilteredItems, setShowFilteredItems] = React.useState(false)
  // const [showFilterButton, setShowFilterButton] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const facets = React.useMemo(() => {
    return props.facets
      .filter(
        (facet) =>
          facet.__typename === Facet.SLIDER ||
          ((facet.__typename === Facet.SIMPLE ||
            facet.__typename === Facet.RANGED) &&
            displayFacet(facet.options)),
      )
      .map((facet) => {
        const sectionCount = props.selectedFacets.find(
          (selection) => selection.facetName === facet.facetName,
        )?.selections.length

        return (
          <StyledBlankDropdown
            quantifier={sectionCount}
            zIndex={ZIndexLevel.Base5}
            width="100%"
            isBrandFacet={facet.facetName.includes('brand_content')}
            dropdownWidth={
              facet.facetName.includes('brand_content') ? '1000px' : undefined
            }
            useDefaultDropdownWidth={!facet.facetName.includes('brand_content')}
            buttonText={facet.facetHeader}
            ariaHasPopup="listbox"
            onCustomButtonClick={() => {
              props.onInputChange({
                facets: clearFacetSelection(
                  facet.facetName,
                  props.selectedFacets,
                ),
              })
            }}
            i18nButtonText={{
              ...props.i18nText,
              customButton: props.i18nText.clearAll,
            }}
            hasBrandSearch={props.hasBrandSearch}
          >
            {facet.__typename === Facet.SIMPLE &&
            facet.facetName.includes('brand_content') ? (
              <BrandFacet
                {...props}
                facet={facet as SimpleFacet}
                hasBrandSearch={true}
              />
            ) : (
              <FacetContent
                isHorizontalFacet
                facet={facet}
                maxHeight="216px"
                selectedFacets={props.selectedFacets}
                onInputChange={props.onInputChange}
                i18nText={props.i18nText}
              />
            )}
          </StyledBlankDropdown>
        )
      })
  }, [props.facets])

  // React.useEffect(() => {
  //   if (containerRef.current) {
  //     const {
  //       clientWidth,
  //       clientHeight,
  //       scrollWidth,
  //       scrollHeight,
  //     } = containerRef.current
  //
  //     setShowFilterButton(
  //       isOverflown({ clientWidth, clientHeight, scrollWidth, scrollHeight }),
  //     )
  //   }
  // }, [facets?.length])

  return (
    <HorizontalFacetWrapper>
      <FacetContainer
        data-testid="horizontal-facet-dropdowns-container"
        isOpen={showFilteredItems}
        ref={containerRef}
      >
        <CustomDropdown
          stickyPosition
          zIndex={ZIndexLevel.Base5}
          placeholder={props.i18nText.sortBy}
          aria-label={props.i18nText.sortBy}
          data-testid="sort-by-dropdown"
          options={props.dropDownOptions}
          useDefaultDropdownWidth
          width={spacing(26.25)}
          onChange={(event) =>
            props.onInputChange({
              sort: props.sortTypes.find((sort) => sort.type === event.key)
                ?.type,
            })
          }
          selected={props.selectedSort}
          hasPlaceholderAsLabel
          hasMarginBottom
        />
        {facets}
      </FacetContainer>
      <ButtonContainer isOpen={showFilteredItems}>
        {props.selectedFacets.length > 0 && (
          <ClearAllButton
            emphasis="low"
            onClick={() => {
              props.onInputChange({
                facets: [],
              })
              setShowFilteredItems(false)
            }}
            data-testid="clear-all-button"
          >
            {props.i18nText.clearAll}
          </ClearAllButton>
        )}
        {/*<FilterButton
          emphasis="low"
          onClick={() => setShowFilteredItems((state) => !state)}
          data-testid="filter-button"
          show={showFilterButton}
        >
          {vsprintf(props.i18nText.filterText, [showFilteredItems ? '-' : '+'])}
        </FilterButton>*/}
      </ButtonContainer>
    </HorizontalFacetWrapper>
  )
}
